// Input
// ------
// Standardised text input. Replaces every ad-hoc Tailwind input string and the
// legacy `.field` CSS class. Use it everywhere you'd otherwise reach for a
// raw <input>.
//
// Variants:
//   - light (default): white background, gray border. Use on authenticated pages.
//   - dark:            translucent white background. Use on the auth pages
//                      (Login, ForgotPassword, ResetPassword, ChangePassword,
//                      ActivateAccount) which sit on the dark NeuralNetworkBackground.
//
// Optional slots:
//   - leftIcon:  An icon node rendered inside the left padding (e.g. <Mail />)
//   - rightSlot: An interactive node rendered inside the right padding
//                (e.g. a password show/hide button)
//
// Error state:
//   - hasError: when true, swaps the border + focus ring to red. Use this from
//               the FormField wrapper rather than passing it directly.

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const inputVariants = cva(
  "flex h-10 w-full rounded-lg border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2",
  {
    variants: {
      variant: {
        light:
          "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500/40 focus:ring-blue-500/40",
        dark:
          "border-white/[0.08] bg-white/[0.04] text-white placeholder:text-white/25 focus:border-blue-500/40 focus:ring-blue-500/40",
      },
      hasError: {
        true: "border-red-500 focus:border-red-500 focus:ring-red-500/40",
        false: "",
      },
    },
    defaultVariants: {
      variant: "light",
      hasError: false,
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  leftIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, hasError, leftIcon, rightSlot, type, ...props }, ref) => {
    // Padding is bumped when an icon/slot is present so the text doesn't overlap.
    const paddingClass = cn(leftIcon && "pl-10", rightSlot && "pr-10");

    const inputElement = (
      <input
        type={type}
        ref={ref}
        className={cn(inputVariants({ variant, hasError }), paddingClass, className)}
        {...props}
      />
    );

    // No icon and no right slot — render the bare input.
    if (!leftIcon && !rightSlot) {
      return inputElement;
    }

    // Wrap in a relative container so absolutely-positioned slots align.
    // The icon color intentionally inherits from the surrounding theme.
    const iconColor = variant === "dark" ? "text-white/30" : "text-gray-300";

    return (
      <div className="relative">
        {leftIcon && (
          <div className={cn("absolute left-3 top-2.5 h-4 w-4 pointer-events-none", iconColor)}>
            {leftIcon}
          </div>
        )}
        {inputElement}
        {rightSlot && (
          <div className={cn("absolute right-3 top-2.5", iconColor)}>{rightSlot}</div>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input, inputVariants };
